import { useQuery } from '@apollo/client';
import { GET_SMART_INBOX_INTEGRATION } from '../../../customer/queries.ts';
import { PERSONAL_SMART_INBOX_INTEGRATIONS } from '../../../users/queries.ts';
import { SmartInboxIntegrationType } from '../../../customer/pages/SmartInbox/types.ts';
import { PersonalSmartInboxIntegrationType } from '../../../users/components/PersonalSmartInbox/types.ts';
import useAgentMailboxLinks from './useAgentMailboxLinks.ts';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import {
    canManageSmartInbox,
    canSeeOthersPersonalMailboxes,
    canUsePersonalSmartInbox,
    isOwnMailbox,
} from '../../../users/utils.ts';

export type MailboxDetailType = 'shared' | 'personal';

interface ConnectedAgent {
    id: string;
    name: string;
}

interface UseMailboxDetailResult {
    loading: boolean;
    notFound: boolean;
    sharedIntegration: SmartInboxIntegrationType | null;
    personalIntegration: PersonalSmartInboxIntegrationType | null;
    connectedAgent: ConnectedAgent | null;
    refetchShared: () => Promise<any>;
}

// Resolves a single mailbox row (shared or personal) by id for the mailbox
// details page, plus the at-most-one agent it's connected to (see the
// "one agent per mailbox" constraint in the plan header).
export const useMailboxDetail = (id: string, type: MailboxDetailType): UseMailboxDetailResult => {
    const { user } = useApplicationContext();
    const {
        data: sharedData,
        loading: sharedLoading,
        refetch: refetchShared,
    } = useQuery(GET_SMART_INBOX_INTEGRATION, {
        variables: { id },
        skip: type !== 'shared' || !id || !canManageSmartInbox(user),
        fetchPolicy: 'cache-and-network',
    });

    // No by-id query exists for personal integrations (only the viewer's own,
    // via `personalSmartInboxIntegration`, and the admin list). Reuse the
    // admin list — already fetched for the Overview page — and find by id.
    const { data: personalData, loading: personalLoading } = useQuery(PERSONAL_SMART_INBOX_INTEGRATIONS, {
        skip: type !== 'personal' || !canUsePersonalSmartInbox(user),
        fetchPolicy: 'cache-and-network',
    });

    const { agentByMailboxId, loading: agentsLoading } = useAgentMailboxLinks();

    const sharedIntegration: SmartInboxIntegrationType | null =
        type === 'shared' ? (sharedData?.smartInboxIntegration ?? null) : null;

    const personalIntegration: PersonalSmartInboxIntegrationType | null =
        type === 'personal'
            ? ((personalData?.personalSmartInboxIntegrations || []).find(
                  (integration: PersonalSmartInboxIntegrationType) =>
                      integration.id === id && (canSeeOthersPersonalMailboxes(user) || isOwnMailbox(user, integration))
              ) ?? null)
            : null;

    const joinedAgent: ConnectedAgent | null = agentByMailboxId.get(id) ?? null;

    const connectedAgent: ConnectedAgent | null =
        type === 'personal' ? (personalIntegration?.agent ?? joinedAgent) : joinedAgent;

    const loading = (type === 'shared' ? sharedLoading : personalLoading) || agentsLoading;
    const notFound = !loading && (type === 'shared' ? !sharedIntegration : !personalIntegration);

    return { loading, notFound, sharedIntegration, personalIntegration, connectedAgent, refetchShared };
};

export default useMailboxDetail;
